import { Button, DataTable, type LocalDataColumn, StatusBadge } from "@nymbl/nos2-rules";
import { Pencil } from "lucide-react";
import { type TemplateSummary, useEmailTemplates } from "@/hooks/use-email";

const STATUS_TONES = { Editable: "success", "Code-only": "neutral" } as const;
const statusOf = (t: TemplateSummary) => (t.editable ? "Editable" : "Code-only");

const COLUMNS: LocalDataColumn<TemplateSummary>[] = [
  {
    key: "label",
    label: "Template",
    type: "text",
    accessor: (t) => t.label,
    render: (t) => <span className="font-medium">{t.label}</span>,
  },
  {
    key: "key",
    label: "Key",
    type: "text",
    accessor: (t) => t.key,
    render: (t) => <span className="font-mono text-xs text-muted-foreground">{t.key}</span>,
  },
  {
    key: "status",
    label: "Status",
    type: "text",
    accessor: statusOf,
    render: (t) => <StatusBadge status={statusOf(t)} tones={STATUS_TONES} />,
  },
  {
    key: "updated",
    label: "Updated",
    type: "date",
    accessor: (t) => t.updatedAt?.slice(0, 10) ?? null,
    render: (t) => (
      <span className="text-muted-foreground">
        {t.updatedAt ? new Date(t.updatedAt).toLocaleString() : "—"}
      </span>
    ),
  },
];

/** The Email section's templates; Edit opens `?template=<key>`. */
export function TemplatesCard({ onEdit }: { onEdit: (key: string) => void }) {
  const templates = useEmailTemplates();

  return (
    <DataTable<TemplateSummary>
      rows={templates.data ?? []}
      loading={templates.isLoading}
      error={
        templates.isError ? (templates.error?.message ?? "Failed to load templates") : undefined
      }
      onRetry={() => templates.refetch()}
      rowKey={(t) => t.key}
      label="Templates"
      title="Templates"
      scope="Templates"
      columns={COLUMNS}
      rowActions={(t) => (
        <Button
          size="sm"
          variant="outline"
          aria-label={`${t.editable ? "Edit" : "View"} ${t.label}`}
          onClick={() => onEdit(t.key)}
          disabled={!t.editable}
        >
          <Pencil className="h-3.5 w-3.5" />
          {t.editable ? "Edit" : "View"}
        </Button>
      )}
    />
  );
}
